import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  enableLostMode,
  disableLostMode,
  playLostModeSound,
  requestDeviceLocation,
} from "../../commands/mdmCommands";

function mapsUrl(location) {
  if (location?.latitude == null || location?.longitude == null) return null;
  return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("de-DE");
}

function LostModeModal({ visible, device, hideLostModeModal, onIssued }) {
  const [show, setShow] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [footnote, setFootnote] = useState("");

  const handleClose = () => {
    setShow(false);
    hideLostModeModal();
  };

  useEffect(() => {
    setShow(visible);
    setPhoneNumber(device?.lostMode?.phoneNumber || "");
    setMessage(device?.lostMode?.message || "");
    setFootnote(device?.lostMode?.footnote || "");
  }, [visible, device]);

  const enabled = Boolean(device?.lostMode?.enabled);
  const location = device?.lostModeLocation;
  const locationLink = mapsUrl(location);

  async function issue(fn, closeAfter = false) {
    try {
      await fn();
      onIssued?.();
      if (closeAfter) handleClose();
    } catch {
      /* Toast kommt aus mdmCommands */
    }
  }

  function sendEnable() {
    const text = message.trim();
    const phone = phoneNumber.trim();
    if (!text && !phone) return;
    issue(() => enableLostMode(device.UDID, {
      message: text,
      phoneNumber: phone,
      footnote: footnote.trim(),
    }), true);
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Lost Mode</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Status: <strong>{enabled ? "aktiv" : "inaktiv"}</strong>
          {enabled && device?.lostMode?.enabledAt
            ? ` seit ${formatWhen(device.lostMode.enabledAt)}`
            : null}
        </p>
        {enabled ? (
          <>
            {device?.lostMode?.message ? <p>Nachricht: {device.lostMode.message}</p> : null}
            {device?.lostMode?.phoneNumber ? <p>Telefon: {device.lostMode.phoneNumber}</p> : null}
            {locationLink ? (
              <p>
                Standort:{" "}
                <a href={locationLink} target="_blank" rel="noreferrer">
                  {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
                </a>
                {location.retrievedAt ? ` (${formatWhen(location.retrievedAt)})` : ""}
              </p>
            ) : (
              <p className="text-muted">Noch kein Standort abgefragt.</p>
            )}
            <p className="form-text">
              Nur auf betreuten iPhones und iPads. Standort und Piepton funktionieren nur im Lost Mode.
            </p>
          </>
        ) : (
          <form>
            <div className="mb-3">
              <label htmlFor="lostModeMessage" className="form-label">
                Nachricht auf dem Sperrbildschirm
              </label>
              <input
                type="text"
                className="form-control"
                id="lostModeMessage"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Dieses Gerät wurde gemeldet"
              />
            </div>
            <div className="mb-3">
              <label htmlFor="lostModePhone" className="form-label">
                Kontakttelefon
              </label>
              <input
                type="text"
                className="form-control"
                id="lostModePhone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="optional, wenn eine Nachricht gesetzt ist"
              />
            </div>
            <div className="mb-3">
              <label htmlFor="lostModeFootnote" className="form-label">
                Fußzeile
              </label>
              <input
                type="text"
                className="form-control"
                id="lostModeFootnote"
                value={footnote}
                onChange={(e) => setFootnote(e.target.value)}
                placeholder="optional"
              />
            </div>
            <div className="form-text">
              Apple verlangt Nachricht oder Telefonnummer. Nur betreute Geräte.
            </div>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Schließen
        </Button>
        {enabled ? (
          <>
            <Button variant="outline-secondary" onClick={() => issue(() => playLostModeSound(device.UDID))}>
              Piepton
            </Button>
            <Button variant="outline-primary" onClick={() => issue(() => requestDeviceLocation(device.UDID))}>
              Standort
            </Button>
            <Button variant="danger" onClick={() => issue(() => disableLostMode(device.UDID), true)}>
              Lost Mode beenden
            </Button>
          </>
        ) : (
          <Button
            variant="danger"
            disabled={!message.trim() && !phoneNumber.trim()}
            onClick={sendEnable}
          >
            Lost Mode aktivieren
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default LostModeModal;
